import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_URL, API_ENDPOINTS } from '../../../constants/api';
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';
import type { MatchTeamKit } from '../types/worldcup.types';

export interface MemberProfileStats {
  /** The SETTLED standing. Unchanged meaning; see `provisionalPoints` for the headline. */
  totalPoints: number;
  exactHitCount: number;
  correctResultCount: number;
  /**
   * Provisional points from matches currently in play (specs/champions-league AC-18.4).
   * Optional because the field is additive — a server that predates it simply omits it, and
   * the profile then reads exactly what it read before.
   */
  livePoints?: number;
  /** `totalPoints + livePoints`. The number the squad leaderboard ranked this member by. */
  provisionalPoints?: number;
}

export interface MemberProfileFutures {
  winnerTeamId: string | null;
  topScorerPlayerId: string | null;
  winnerCorrect: boolean | null;
  topScorerCorrect: boolean | null;
}

export interface MemberProfileMatch {
  competitionMatchId: string;
  matchId: string;
  homeTeam: string;
  awayTeam: string;
  homeCountryCode: string;
  awayCountryCode: string;
  /** Per-club kit (AC-3.8); absent from a server that predates the field. */
  homeKit?: MatchTeamKit | null;
  awayKit?: MatchTeamKit | null;
  kickoffTime: string;
  status: 'finished' | 'live';
  stage: string;
  stageLabel: string;
  round: string;
  displayOrder: number | null;
  homeScore: number | null;
  awayScore: number | null;
  fullTimeHomeScore?: number | null;
  fullTimeAwayScore?: number | null;
  penaltyHomeScore?: number | null;
  penaltyAwayScore?: number | null;
  liveMinute: number | null;
  prediction: {
    home: number;
    away: number;
    isMonkeyPick: boolean;
    pointsEarned: number | null;
    /**
     * Provisional points for a match still in play (AC-18.6). Null on a finished match and on
     * any match the member did not pick. Separate from `pointsEarned`, which stays "what
     * settlement recorded" — a live match has none until full-time.
     */
    livePointsEarned?: number | null;
    /** True when a golden ball doubled this pick's points (×2). */
    isBoosted: boolean;
  } | null;
}

export interface MemberProfileData {
  user: {
    id: string;
    name: string;
    avatarId: string | null;
    avatarConfig?: EquippedMap | null;
    avatar: { url: string } | null;
  };
  stats: MemberProfileStats;
  futures: MemberProfileFutures | null;
  /**
   * True when the target's futures are withheld until the first match kicks off
   * (peers only — your own profile always shows your picks). The UI renders a
   * "locked" state instead of "not picked".
   */
  futuresHidden?: boolean;
  matches: MemberProfileMatch[];
}

type ApiInvoker = <T>(endpoint: string, options?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;

interface MemberProfileApiResponse {
  success: boolean;
  data: MemberProfileData;
}

/** Group-scoped: viewer and target must share the group. */
export async function fetchMemberProfile(
  apiClient: ApiInvoker,
  competitionId: string,
  groupId: string,
  memberId: string,
): Promise<MemberProfileData> {
  const url = `${API_URL}${API_ENDPOINTS.competitions.memberProfile}`
    .replace(':competitionId', encodeURIComponent(competitionId))
    .replace(':groupId', encodeURIComponent(groupId))
    .replace(':memberId', encodeURIComponent(memberId));
  const response = await apiClient<MemberProfileApiResponse>(url);
  if (!response.data.success) {
    throw new Error('Failed to fetch member profile');
  }
  return response.data.data;
}

/** Global: target only needs to be a competition participant (for global leaderboard). */
export async function fetchParticipantProfile(
  apiClient: ApiInvoker,
  competitionId: string,
  memberId: string,
): Promise<MemberProfileData> {
  const url = `${API_URL}${API_ENDPOINTS.competitions.participantProfile}`
    .replace(':competitionId', encodeURIComponent(competitionId))
    .replace(':memberId', encodeURIComponent(memberId));
  const response = await apiClient<MemberProfileApiResponse>(url);
  if (!response.data.success) {
    throw new Error('Failed to fetch participant profile');
  }
  return response.data.data;
}
